/**
 * btk-vat-toggle.js
 * Better Trade Kitchens - Global VAT Toggle Utility
 *
 * Loaded deferred from theme.liquid before btk-basket-api.js.
 * Provides window.BTKVat for use by all other BTK price-rendering scripts.
 *
 * State key: 'btk_inc_vat' in localStorage.  Default: true (Inc VAT).
 * Toggle fires the custom event 'btk:vat:changed' on document.
 *
 * Price elements can opt into automatic DOM-walk updates by carrying:
 *   data-btk-raw="[pence]"       - main price (integer pence)
 *   data-btk-raw-was="[pence]"   - struck-through WAS price (integer pence)
 *
 * Basket and drawer modules listen for btk:vat:changed and call render()
 * themselves rather than relying on the DOM walk.
 */

window.BTKVat = (function () {
  var KEY     = 'btk_inc_vat';
  var DIVISOR = 1.2;

  // ─── STATE ──────────────────────────────────────────────────────────────────

  function isIncVat() {
    try {
      var v = localStorage.getItem(KEY);
      return v === null ? true : v === 'true';
    } catch (e) { return true; }
  }

  function setIncVat(v) {
    try { localStorage.setItem(KEY, String(!!v)); } catch (e) {}
  }

  // ─── FORMATTING ─────────────────────────────────────────────────────────────

  /**
   * formatPrice(pence)
   * Accepts an integer pence value (as used by Shopify variant.price).
   * Returns a formatted GBP string, dividing by 1.2 when Exc VAT is active.
   */
  function formatPrice(pence) {
    var amount = isIncVat()
      ? (Number(pence) || 0) / 100
      : (Number(pence) || 0) / 100 / DIVISOR;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP',
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(amount);
  }

  // ─── THE THEME'S OWN PRICES ─────────────────────────────────────────────────
  // Prices drawn by the theme's price snippet (snippets/price.liquid: standard
  // product pages and product cards) stayed inc VAT whatever the switch said
  // (bug sheet Non Urgent row 58). They carry inc VAT pence in
  // data-btk-vat-price and are shown the switch's way from page load, and
  // again whenever the theme redraws them - a size or option change replaces
  // the whole price block (variant-picker.js). Kept apart from data-btk-raw,
  // whose owners draw their own text (e.g. "Was <span>...").

  function applyPriceSnippets() {
    document.querySelectorAll('[data-btk-vat-price]').forEach(function (el) {
      var raw = parseInt(el.getAttribute('data-btk-vat-price'), 10);
      if (isNaN(raw)) return;
      var text = formatPrice(raw);
      if (el.textContent !== text) el.textContent = text;
    });
  }

  var WATCHED = '[data-btk-vat-price],[data-btk-vat-exonly],[data-btk-vat-inconly]';
  var redrawQueued = false;
  function watchRedraws() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (list) {
      if (redrawQueued) return;
      for (var i = 0; i < list.length; i++) {
        var added = list[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;    // text: our own redraws, never a loop
          if ((n.matches && n.matches(WATCHED)) || (n.querySelector && n.querySelector(WATCHED))) {
            redrawQueued = true;
            (window.requestAnimationFrame || setTimeout)(function () {
              redrawQueued = false;
              applyPriceSnippets();
              updateToggleUI();
            });
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ─── TOGGLE ─────────────────────────────────────────────────────────────────

  function toggle() {
    setIncVat(!isIncVat());
    document.dispatchEvent(new CustomEvent('btk:vat:changed', {
      bubbles: true,
      detail: { incVat: isIncVat() }
    }));
    updateToggleUI();
    applyToDOM();
    applyPriceSnippets();
  }

  // ─── DOM WALK (listing page / PDP static price elements) ────────────────────

  function applyToDOM() {
    document.querySelectorAll('[data-btk-raw]').forEach(function (el) {
      var raw = parseInt(el.getAttribute('data-btk-raw'), 10);
      if (!isNaN(raw)) el.textContent = formatPrice(raw);
    });
    document.querySelectorAll('[data-btk-raw-was]').forEach(function (el) {
      var raw = parseInt(el.getAttribute('data-btk-raw-was'), 10);
      if (!isNaN(raw)) el.textContent = 'WAS ' + formatPrice(raw);
    });
  }

  // ─── TOGGLE UI ───────────────────────────────────────────────────────────────
  // Applied to every element carrying [data-btk-vat-toggle].

  function updateToggleUI() {
    var inc = isIncVat();
    document.querySelectorAll('[data-btk-vat-toggle]').forEach(function (track) {
      track.classList.toggle('is-right', !inc);
      track.querySelectorAll('.btk-vat-btn').forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-vat-val') === (inc ? 'inc' : 'exc'));
      });
    });
    // Read-only readouts: surfaces that REPORT the setting without offering a
    // control. The planner's black utility bar is one - its only VAT control is
    // the basket's Price Breakdown toggle. Text only, no .btk-vat-btn, so the
    // click delegation above never picks these up.
    document.querySelectorAll('[data-btk-vat-status]').forEach(function (el) {
      el.textContent = inc ? 'Inc VAT' : 'Exc VAT';
    });
    // Marks that only show while prices are without VAT - the header basket
    // total's "ex VAT" (snippets/icon-cart.liquid).
    document.querySelectorAll('[data-btk-vat-exonly]').forEach(function (el) {
      el.hidden = inc;
    });
    // ...and the ones for Inc VAT only - the product page's "Tax included".
    document.querySelectorAll('[data-btk-vat-inconly]').forEach(function (el) {
      el.hidden = !inc;
    });
  }

  // ─── CLICK DELEGATION ────────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.btk-vat-btn');
    if (!btn) return;
    var val = btn.getAttribute('data-vat-val');
    if (!val) return;
    if ((val === 'inc') !== isIncVat()) toggle();
  });

  // ─── INIT ────────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    updateToggleUI();
    applyPriceSnippets();
    watchRedraws();
  });

  return {
    isIncVat:        isIncVat,
    formatPrice:     formatPrice,
    toggle:          toggle,
    updateToggleUI:  updateToggleUI
  };

})();